import { useState, useEffect, useCallback } from "react";
import { FaSearch, FaFileSignature, FaEdit } from 'react-icons/fa';
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import DataTable from 'react-data-table-component';
import '../common/style.css';
import './project.css';
import { useNavigate } from 'react-router-dom';
import { showProjects } from "../http/api";
import Loading from "../loading/loading";
import { useAuth } from "../http/authcontex";

export default function ShowProjects() {
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const authContext = useAuth();

    const [isSidebarOpen, setIsSidebarOpen] = useState(
        localStorage.getItem('sb|sidebar-toggle') === 'true'
    );

    const [projects, setProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProjects, setFilteredProjects] = useState([]);

    useEffect(() => {
        localStorage.setItem('sb|sidebar-toggle', isSidebarOpen);
    }, [isSidebarOpen]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
        localStorage.setItem('sb|sidebar-toggle', !isSidebarOpen);
    };

    const viewProjectData = useCallback(() => {
        setLoading(true);
        showProjects()
            .then(response => {
                if (response.status === 200) {
                    const projectData = response.data.map(item => ({
                        id: item.id,
                        active: item.active,
                        details: item.details,
                        locations: item.locations,
                        name: item.name,
                        print_address: item.print_address,

                        // if API already returns these extra fields, keep them
                        projectType: item.projectType,
                        startDate: item.startDate,
                        endDate: item.endDate,
                        subCategory: item.subCategory,
                        subScheme: item.subScheme,
                        targetNumber: item.targetNumber,
                        trainingType: item.trainingType,
                        fundedBy: item.fundedBy,
                    }));

                    setProjects(projectData);
                    setLoading(false);
                }
            })
            .catch(error => {
                setLoading(false);
                if (error.response && error.response.status === 401) {
                    console.error('Unauthorized access, redirecting to login...');
                    authContext.logout();
                    navigate('');
                } else {
                    console.error('Error fetching project details:', error);
                }
            });
    }, [authContext, navigate]);

    useEffect(() => {
        viewProjectData();
    }, [viewProjectData]);

    // Filter projects whenever searchTerm or projects change
    useEffect(() => {
        const filtered = projects.filter(project =>
            Object.values(project).some(value =>
                typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredProjects(filtered);
    }, [searchTerm, projects]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleAdd = () => {
        navigate("/editProject");  // navigate to add project page
    };

    const handleEdit = (project) => {
        navigate(`/editProject/${project.id}`);  // navigate to edit project page
    };

    const columns = [
        {
            name: 'Project Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Active',
            selector: row => row.active ? 'Active' : 'Inactive',
            sortable: true,
        },
        {
            name: 'Details',
            selector: row => row.details,
            sortable: true,
        },
        {
            name: 'Location',
            selector: row => row.locations,
            sortable: true,
        },
        {
            name: 'Date',
            selector: row => row.startDate,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row) => (
                <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEdit(row)}
                >
                    <FaEdit /> Edit
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    return (
        <div className="ShowProjects">
            {loading && <Loading />}
            <div className={`sb-nav-fixed ${isSidebarOpen ? 'sb-sidenav-toggled' : ''}`}>
                <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
                <div id="layoutSidenav">
                    <SideNave isSidebarOpen={isSidebarOpen} />
                    <div id="layoutSidenav_content">
                        <main>
                            <div className="container-fluid px-4">
                                <h4 className="mt-4">Projects</h4>
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <button className="btn btn-sm btn-success me-2" onClick={handleAdd}>
                                                <FaFileSignature /> Add Projects
                                            </button>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                className="form-control me-2"
                                                value={searchTerm}
                                                onChange={handleSearch}
                                            />
                                            <button className="btn btn-sm btn-secondary">
                                                <FaSearch />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="card mb-4">
                                    <div className="card-header">
                                        <i className="fas fa-table me-1"></i>
                                        Project Details
                                    </div>
                                    <div className="card-body shadow-sm border-0">
                                        <DataTable
                                            className="data-table"
                                            columns={columns}
                                            data={filteredProjects}
                                            pagination
                                            highlightOnHover
                                        />
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
}
